/**
 * Client-side Word document-to-Markdown extraction built on mammoth + turndown.
 *
 * Primary API:
 * - wordDownloadUrlToMarkdown(downloadUrl, options): fetches a .docx URL and returns Markdown.
 * - arrayBufferToWordMarkdown(arrayBuffer, options): converts an already-fetched .docx ArrayBuffer.
 * - wordHtmlToMarkdown(html, options): converts Mammoth's HTML fragment to Markdown.
 *
 * Options:
 * - mammoth: optional object spread into mammoth.convertToHtml({ ... }, mammothOptions).
 *   Useful for styleMap, includeDefaultStyleMap, ignoreEmptyParagraphs, externalFileAccess, etc.
 * - turndown: optional TurndownService options, such as headingStyle or bulletListMarker.
 * - fetchFn: optional fetch-compatible function used only by wordDownloadUrlToMarkdown.
 * - includeImages: false by default. Mammoth embeds images as base64 HTML by default, which is noisy
 *   for text extraction and can create huge Markdown output. Set true to keep Mammoth's default image
 *   behavior, or provide mammoth.convertImage for custom image handling.
 *
 * Limitations:
 * - Supports .docx files. Legacy .doc binaries are not supported by Mammoth.
 * - Mammoth is semantic, not layout-exact. It preserves headings, lists, links, basic formatting,
 *   tables, notes, comments, and line breaks where the .docx exposes them cleanly.
 */
import TurndownService from "turndown";

const DEFAULT_TURNDOWN_OPTIONS = {
	headingStyle: "atx",
	bulletListMarker: "-",
	codeBlockStyle: "fenced"
};

const createTurndownService = (options = {}) =>
	new TurndownService({
		...DEFAULT_TURNDOWN_OPTIONS,
		...options
	});

const getMammothOptions = (options = {}) => {
	if (options.includeImages || options.mammoth?.convertImage) return options.mammoth || {};

	return {
		...options.mammoth,
		convertImage: () => []
	};
};

const getMammothInput = (arrayBuffer) => {
	if (typeof Buffer !== "undefined") return { buffer: Buffer.from(arrayBuffer) };
	return { arrayBuffer };
};

export const wordHtmlToMarkdown = (html = "", options = {}) => {
	if (!html) return "";
	return createTurndownService(options.turndown).turndown(html).trim();
};

export const arrayBufferToWordMarkdown = async (arrayBuffer, options = {}) => {
	const mammoth = await import("mammoth");
	const mammothOptions = getMammothOptions(options);
	const result = await mammoth.convertToHtml(getMammothInput(arrayBuffer), mammothOptions);
	const markdown = wordHtmlToMarkdown(result.value, options);

	return {
		markdown,
		messages: result.messages || []
	};
};

export const wordDownloadUrlToMarkdown = async (downloadUrl, options = {}) => {
	if (!downloadUrl) throw new Error("wordDownloadUrlToMarkdown: downloadUrl is required");

	const fetchFn = options.fetchFn ?? fetch;
	const response = await fetchFn(downloadUrl);

	if (!response.ok) {
		throw new Error(`Failed to fetch Word document: ${response.status} ${response.statusText}`);
	}

	const contentType = response.headers?.get?.("content-type") || "";
	const validContentType =
		contentType.includes("application/vnd.openxmlformats-officedocument.wordprocessingml.document") ||
		contentType.includes("application/octet-stream") ||
		contentType.includes("binary/octet-stream");

	if (contentType && !validContentType) {
		console.warn(`wordDownloadUrlToMarkdown: expected .docx content type, received ${contentType}`);
	}

	const arrayBuffer = await response.arrayBuffer();
	const { markdown } = await arrayBufferToWordMarkdown(arrayBuffer, options);
	return markdown;
};
